import React from 'react';
import { Navigate } from 'react-router-dom';
import { redirectUnlessInventory } from '../../utils/moduleAccess';
import { Form, FormGroup, FormControl, Button, Row, Col } from 'react-bootstrap';
import SharedDataTable from '../../_components/Shared/SharedDataTable';
import config from '../../../config';
import moment from 'moment';
import { handleFocusInScrollField } from './invKeyboardScroll';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import {
    InventorySummaryExportTable,
    FG_TRANSFER_SUMMARY_EXPORT_COLUMNS,
    exportInventorySummary,
    mapFgTransferToExportRow,
} from './invSummaryExport';

function normalizeDateFilterInput(input) {
    const raw = String(input || '').trim();
    if (!raw) return '';
    const ddmmyyyy = moment(raw, 'DD/MM/YYYY', true);
    if (ddmmyyyy.isValid()) return ddmmyyyy.format('YYYY-MM-DD');
    const iso = moment(raw, 'YYYY-MM-DD', true);
    if (iso.isValid()) return iso.format('YYYY-MM-DD');
    return '';
}

class FGTransferSummary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            loading: false,
            company: 0,
            searchKeyword: '',
            searchTransferDate: '',
        };
    }

    componentDidMount() {
        this.loadUserData();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.company !== this.state.company && this.state.company) {
            this.loadTransferData();
        }
    }

    loadUserData = () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            if (userData && userData.accountId) {
                this.setState({ company: userData.accountId }, () => {
                    this.loadTransferData();
                });
            }
        } catch (error) {
            console.error('Error loading user data:', error);
        }
    }

    loadTransferData = async () => {
        if (!this.state.company) return;

        this.setState({ loading: true });
        try {
            const url = `${config.apiUrl}/getFGMaterialTransfers?company=${this.state.company}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            
            let data = await response.json();
            
            // Filter by search keyword if provided (searches across partNo, fromLocationCode, toLocationCode)
            if (this.state.searchKeyword && Array.isArray(data)) {
                const keyword = this.state.searchKeyword.toLowerCase();
                data = data.filter(item => {
                    const partNo = (item.partNo || '').toLowerCase();
                    const fromLocation = (item.fromLocationCode || '').toLowerCase();
                    const toLocation = (item.toLocationCode || '').toLowerCase();
                    
                    return partNo.includes(keyword) || 
                           fromLocation.includes(keyword) || 
                           toLocation.includes(keyword);
                });
            }
            
            // Filter by transfer date if provided
            if (this.state.searchTransferDate && Array.isArray(data)) {
                const searchDate = normalizeDateFilterInput(this.state.searchTransferDate);
                if (!searchDate) {
                    data = [];
                } else {
                data = data.filter(item => {
                    if (item.createdAt) {
                        const itemDate = moment(item.createdAt).format('YYYY-MM-DD');
                        return itemDate === searchDate;
                    }
                    return false;
                });
                }
            }
            
            if (Array.isArray(data)) {
                const formattedData = data.map((item, index) => {
                    const reportNo = item.createdAt && item.id 
                        ? moment(item.createdAt).format('YYYYMMDD') + 'T' + item.id
                        : item.id || '-';
                    
                    return {
                        no: index + 1,
                        reportNo: reportNo,
                        id: item.id,
                        partNo: item.partNo || '-',
                        description: item.description || '-',
                        qty: item.quantity || 0,
                        transferDateTime: item.createdAt 
                            ? moment(item.createdAt).format('DD/MM/YYYY')
                            : '-',
                        transactionId: item.transactionId || '-',
                        from: item.fromLocationCode || '-',
                        fromDescription: item.fromLocationDescription || '',
                        to: item.toLocationCode || '-',
                        toDescription: item.toLocationDescription || '',
                        status: item.status === 'rejected' 
                            ? `Rejected. ${item.rejectReason || 'No reason provided'}`
                            : item.status === 'confirmed' 
                                ? 'Confirmed'
                                : item.status === 'pending_confirmation'
                                    ? 'Pending Confirmation'
                                    : item.status || 'Pending',
                        actionDateTime: (item.status === 'confirmed' || item.status === 'rejected') && item.updatedAt
                            ? moment(item.updatedAt).format('DD/MM/YYYY')
                            : '-',
                        sourceItem: item,
                    };
                });
                this.setState({ data: formattedData, loading: false });
            } else {
                this.setState({ data: [], loading: false });
            }
        } catch (error) {
            console.error('Error loading transfer data:', error);
            this.setState({ loading: false });
        }
    }

    handleSearch = () => {
        this.loadTransferData();
    }

    handleReset = () => {
        this.setState({
            searchKeyword: '',
            searchTransferDate: '',
        }, () => {
            this.loadTransferData();
        });
    }

    exportExcel = () => {
        const rows = (this.state.data || []).map((row, index) =>
            mapFgTransferToExportRow(row.sourceItem || row, index)
        );
        if (!rows.length) {
            alert('No records to export. Adjust filters or refresh the list.');
            return;
        }
        exportInventorySummary('fg-transfer-summary-export', 'FG Transfer Summary', 'FG_Transfer_Summary');
    }

    getExportRows = () => (this.state.data || []).map((row, index) =>
        mapFgTransferToExportRow(row.sourceItem || row, index)
    )

    columns = [
        {
            name: 'No.',
            selector: row => row.no,
            width: '100px',
            sortable: true,
        },
        {
            name: 'Report No',
            selector: row => row.reportNo,
            width: '180px',
            sortable: true,
            cell: (row) => (
                <div>
                    <a 
                        href="#"
                        style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (row.id) {
                                window.location.hash = `#/fg-transfer-summary/${row.id}`;
                            }
                        }}
                    >
                        {row.reportNo}
                    </a>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            ignoreRowClick: true,
            allowOverflow: true,
        },
        {
            name: 'Part No',
            selector: row => row.partNo,
            width: '120px',
            sortable: true,
        },
        {
            name: 'Description',
            selector: row => row.description,
            width: '200px',
            sortable: true,
            wrap: true,
        },
        {
            name: 'Qty',
            selector: row => row.qty,
            width: '80px',
            sortable: true,
            right: true,
        },
        {
            name: 'Material Transfer Datetime',
            selector: row => row.transferDateTime,
            width: '180px',
            sortable: true,
        },
        {
            name: 'Transaction ID',
            selector: row => row.transactionId,
            width: '150px',
            sortable: true,
        },
        {
            name: 'From Location',
            selector: row => row.from,
            width: '150px',
            sortable: true,
            cell: (row) => (
                <div>
                    {row.from}
                    {row.fromDescription ? <div style={{ fontSize: '11px', color: '#666' }}>{row.fromDescription}</div> : null}
                </div>
            ),
            wrap: true,
        },
        {
            name: 'To Location',
            selector: row => row.to,
            width: '150px',
            sortable: true,
            cell: (row) => (
                <div>
                    {row.to}
                    {row.toDescription ? <div style={{ fontSize: '11px', color: '#666' }}>{row.toDescription}</div> : null}
                </div>
            ),
            wrap: true,
        },
        {
            name: 'Status',
            selector: row => row.status,
            width: '180px',
            sortable: true,
            wrap: true,
            cell: (row) => (
                <span style={{
                    color: row.status.includes('Rejected') ? '#dc3545' : 
                           row.status === 'Confirmed' ? '#28a745' : 
                           row.status === 'Pending Confirmation' ? '#ffc107' : '#6c757d',
                    fontWeight: '500'
                }}>
                    {row.status}
                </span>
            ),
        },
        {
            name: 'Action Datetime',
            selector: row => row.actionDateTime,
            width: '180px',
            sortable: true,
        },
    ];

    render() {
        const accessDenied = redirectUnlessInventory(localStorage.getItem('user'));
        if (accessDenied) return accessDenied;

        return (
            <div className="container" onFocusCapture={handleFocusInScrollField}>
                <div style={{ marginTop: '20px' }}>
                    <h4>FG Transfer Summary</h4>
                    <p className="text-muted">View and manage FG transfer records</p>
                    
                    <div style={{ marginBottom: '20px', marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                        <Form>
                            <Row className="align-items-end">
                                <Col sm={12} md={5}>
                                    <FormGroup>
                                        <FormControl
                                            type="text"
                                            placeholder="Search by Part No, From Location, or To Location"
                                            value={this.state.searchKeyword}
                                            onChange={(e) => this.setState({ searchKeyword: e.target.value })}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={12} md={3}>
                                    <FormGroup>
                                        <FormControl
                                            type="text"
                                            placeholder="Transfer Date (DD/MM/YYYY)"
                                            value={this.state.searchTransferDate}
                                            onChange={(e) => this.setState({ searchTransferDate: e.target.value })}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={12} md={4}>
                                    <Button
                                        variant="primary"
                                        onClick={this.handleSearch}
                                        style={{ marginRight: '10px' }}
                                        disabled={this.state.loading}
                                    >
                                        Search
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={this.handleReset}
                                        style={{ marginRight: '10px' }}
                                        disabled={this.state.loading}
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        variant="info"
                                        onClick={this.loadTransferData}
                                        disabled={this.state.loading}
                                        style={{ marginRight: '10px' }}
                                    >
                                        {this.state.loading ? 'Loading...' : 'Refresh'}
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={this.exportExcel}
                                        disabled={this.state.loading || !this.state.data.length}
                                    >
                                        Export Excel
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                <SharedDataTable
                    columns={this.columns}
                    data={this.state.data}
                    progressPending={this.state.loading}
                    progressComponent={<DatacoLoadingIndicator loading={this.state.loading} size="compact" label="Loading finished goods transfers" />}
                    pagination
                    paginationPerPage={30}
                    paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                    responsive
                    noDataComponent="No transfer records found"
                />

                <InventorySummaryExportTable
                    tableId="fg-transfer-summary-export"
                    columns={FG_TRANSFER_SUMMARY_EXPORT_COLUMNS}
                    rows={this.getExportRows()}
                />
                </div>
            </div>
        );
    }
}

export { FGTransferSummary };
